import { defineStore } from 'pinia'
import { ref } from 'vue'

import type { ReportViewConfig } from '../models/reportViewConfig.types'

/** Workspace-scoped cache for the stable table-view records (`get_table_view_configs`).
 *  Persisted so a hard reload reuses the last-fetched workspace's records instead of
 *  re-requesting them on every mount of `useReportViewConfigs`. */
export const useReportViewConfigStore = defineStore(
  'reportViewConfigStore',
  () => {
    const workspaceId = ref<number | string | null>(null)
    const configs = ref<ReportViewConfig[]>([])
    const fetchedAt = ref<number | null>(null)

    /** True once a fetch for this exact workspace has completed — not merely started
     *  (`workspaceId` alone would race a second consumer mounting mid-fetch). */
    function ownsFreshWorkspace(id: number | string): boolean {
      return workspaceId.value === id && fetchedAt.value !== null
    }

    function setConfigs(id: number | string, next: readonly ReportViewConfig[]): void {
      workspaceId.value = id
      configs.value = [...next]
      fetchedAt.value = Date.now()
    }

    /** Caller already confirmed `id` is the current workspace (server-acked write) — adopts it
     *  as authoritative without marking the cache "fresh": a single upserted record is not a
     *  full list, so a later mount still refetches to reconcile. */
    function upsertConfig(id: number | string, config: ReportViewConfig): void {
      const sameWorkspace = workspaceId.value === id
      workspaceId.value = id
      configs.value = [
        ...(sameWorkspace ? configs.value : []).filter((item) => item.tableId !== config.tableId),
        config,
      ]
      if (!sameWorkspace) fetchedAt.value = null
    }

    function resetState(): void {
      workspaceId.value = null
      configs.value = []
      fetchedAt.value = null
    }

    return {
      workspaceId,
      configs,
      fetchedAt,
      ownsFreshWorkspace,
      setConfigs,
      upsertConfig,
      resetState,
    }
  },
  {
    persist: {
      key: 'reportViewConfigStore',
      pick: ['workspaceId', 'configs', 'fetchedAt'],
    },
  },
)

export type ReportViewConfigStore = ReturnType<typeof useReportViewConfigStore>
